import React, { useCallback, useEffect, useState } from 'react';
import VormatsAPI from 'common/apis/VormatsAPI';
import MyLibraryButton from 'engage/components/MyLibraryButton/MyLibraryButton';
import cx from 'classnames';
import styles from './Templates.module.scss';
import { useTranslation } from 'react-i18next';
import { TemplatesList } from '../TemplatesList/TemplatesList';
import SearchComponent from 'engage/components/SearchComponent/SearchComponent';
import Spinner from 'common/components/Spinner';
import { EditTemplateModal } from '../EditTemplateModal/EditTemplateModal';
import Pagination from 'engage/components/Pagination/Pagination';
import usePagination from 'engage/hooks/usePagination';
import { ToastNotification } from 'engage/components/Toast/Toast';
import { useHistory } from 'react-router-dom';
import RecordNotFound from 'engage/components/_empty_state/RecordNotFound';

const perPage = 16;

const Templates = ({ companyId }) => {
    const [state, setState] = useState("0"); // 0 - all, 1- default, 2 - company
    const [isEditModalOpen, setEditModalOpen] = React.useState(false);
    const [selectedTemplate, setSelectedTemplate] = React.useState(null);
    const [showSpinner, setShowSpinner] = useState(false);
    const history = useHistory();
    const { t } = useTranslation();

    const fetchTemplates = useCallback(async (params) => {
        const templatesStateFilter = {
        }
        if (state == '1') {
            templatesStateFilter.onlyDefault = true;
        }
        if (state == '2') {
            templatesStateFilter.onlyCompany = true;
        }
        const response = await VormatsAPI.getTemplates({ companyId, ...params, ...templatesStateFilter });
        return response;
    }, [state, companyId]);


    const {
        query,
        list: templates,
        totalData,
        isLoading,
        loadList,
        onChangePage,
        onResetQuery,
        onSearch,
    } = usePagination({
        service: fetchTemplates,
        sortByFiled: '',
        itemsPerPage: perPage,
    });


    const options = [
        {
            name: 'advanced_editor.templates_overlay.filter.all',
            component: <div>{t('advanced_editor.templates_overlay.filter.all')}</div>,
        },
        {
            name: 'advanced_editor.templates_overlay.filter.default',
            component: <div>{t('advanced_editor.templates_overlay.filter.default')}</div>,
        },
        {
            name: 'advanced_editor.templates_overlay.filter.company',
            component: <div>{t('advanced_editor.templates_overlay.filter.company')}</div>,
        }]

    const handleEditTemplate = useCallback((templateId) => {
        const template = templates.find(t => t.id === templateId);
        if (!template) return;
        setSelectedTemplate(template);
        setEditModalOpen(true);
    }, [templates]);

    const handleDeleteTemplate = useCallback(async (templateId) => {
        const template = templates.find(t => t.id === templateId);
        if (!template) return;
        const confirmDelete = window.confirm(t('company_settings.templates.confirm_delete', { name: template.name }));
        if (!confirmDelete) return;

        try {
            await VormatsAPI.deleteTemplate(template.id);
            loadList(true);
        } catch (error) {
            console.error("Error deleting template:", error);
            ToastNotification({
                message: t('general.toast.templates.deleting_error'),
                iconName: 'error',
                isMobileView: false,
            });
        }
    }, [templates, loadList, t]);

    const handleUpdateTemplate = async (id, params) => {
        try {
            let newThumbnailPath = null;
            if (params.thumbnail) {
                const fileExtension = params.thumbnail.name.split('.').pop();
                const { url, fields } = await VormatsAPI.getTemplateThumbnailUploadUrl(id, fileExtension);
                if (url && fields) {
                    newThumbnailPath = await VormatsAPI.uploadPostForm(params.thumbnail, url, fields);
                    params.thumbnail = newThumbnailPath; // Update the thumbnail path in params
                }
            }
            await VormatsAPI.updateTemplate(id, params);
            loadList(true);
        } catch (error) {
            console.error("Error updating template:", error);
        }
    }


    const handleChangeOption = (optionKey) => {
        setState(optionKey);
        // Here you can add logic to fetch templates based on the selected option
        // For example, if optionKey is 'default', fetch default templates
        // If it's 'company', fetch company-specific templates
    }


    const handleRedirect = (templateId) => {
        history.push(`/eg/advanced-editor/templates/${templateId}`);
    };

    useEffect(() => {
        onResetQuery();
    }, [companyId, state]);

    // Handle delayed spinner display
    useEffect(() => {
        let timeoutId;

        if (isLoading) {
            timeoutId = setTimeout(() => {
                setShowSpinner(true);
            }, 200);
        } else {
            setShowSpinner(false);
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [isLoading]);

    return (
        <div className={styles.templatesContainer}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h2 className={styles.title}>{t('advanced_editor.templates_overlay')}</h2>
                    <div className={styles.tabs}>
                        {Object.entries(options).map(([optionKey, optionValue]) => {
                            return <MyLibraryButton
                                key={optionKey}
                                className={cx(styles.tabButton, { [styles.activeTab]: state === optionKey })}
                                text={t(optionValue?.name)}
                                onClick={() => handleChangeOption(optionKey)}
                            />
                        })}
                    </div>
                </div>
                <SearchComponent searchValue={query.search} onChange={onSearch} clearButton />
            </div>

            <TemplatesList templates={templates} query={query} onEdit={handleEditTemplate} onRemove={handleDeleteTemplate} onRedirect={handleRedirect} />
            {totalData > perPage && <div className={styles.paginationContainer}>
                <Pagination
                    currentPage={query.page}
                    totalPages={Math.ceil(totalData / perPage)}
                    changePage={onChangePage}
                />
            </div>}
            {showSpinner && <Spinner className={styles.spinner} width="50px" height="50px" size="4px" />}
            {!isLoading && totalData === 0 && <RecordNotFound searchTerm={query.search} />}
            <EditTemplateModal companyId={companyId} open={isEditModalOpen} onSave={handleUpdateTemplate} onClose={() => setEditModalOpen(false)} template={selectedTemplate} />
        </div>
    );
}

export default Templates;
